import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Box, Button, CircularProgress, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { Replay as ReplayIcon, WhatsApp as WhatsAppIcon, ArrowForward as NextIcon } from '@mui/icons-material';
import QuizCard from '../../components/QuizCard';
import { dictionaryService } from '../../services/api';
import { trackQuizPlay } from '../../utils/trackPageView';

const ROUNDS = 5;
const TEXTS = {
  fr: {
    title: 'Quiz du dictionnaire duala',
    question: 'Quel mot duala correspond à cette image ?',
    right: 'Bravo !', wrong: 'Pas tout à fait. La bonne réponse :', means: 'Sens',
    next: 'Question suivante', finish: 'Voir mon score', replay: 'Rejouer', share: 'Partager sur WhatsApp',
    score: (s, n) => `${s} sur ${n}`,
    verdict: (s, n) => (s === n ? 'Parfait, vous êtes un champion du duala !' : s >= Math.ceil(n / 2) ? 'Très bien, continuez !' : 'Courage, rejouez pour vous améliorer.'),
    shareText: (s, n, url) => `J'ai eu ${s} sur ${n} au quiz du dictionnaire duala. Et vous ?\n\n${url}`,
    loading: 'Préparation du quiz…', error: "Impossible de préparer le quiz pour l'instant. Réessayez plus tard.",
  },
  en: {
    title: 'Duala dictionary quiz',
    question: 'Which Duala word matches this picture?',
    right: 'Well done!', wrong: 'Not quite. The right answer:', means: 'Meaning',
    next: 'Next question', finish: 'See my score', replay: 'Play again', share: 'Share on WhatsApp',
    score: (s, n) => `${s} out of ${n}`,
    verdict: (s, n) => (s === n ? 'Perfect, you are a Duala champion!' : s >= Math.ceil(n / 2) ? 'Very good, keep going!' : 'Keep trying, play again to improve.'),
    shareText: (s, n, url) => `I scored ${s} out of ${n} on the Duala dictionary quiz. What about you?\n\n${url}`,
    loading: 'Preparing the quiz…', error: 'The quiz cannot be prepared right now. Please try again later.',
  },
  es: {
    title: 'Quiz del diccionario duala',
    question: '¿Qué palabra duala corresponde a esta imagen?',
    right: '¡Muy bien!', wrong: 'No exactamente. La respuesta correcta:', means: 'Significado',
    next: 'Siguiente pregunta', finish: 'Ver mi puntuación', replay: 'Jugar de nuevo', share: 'Compartir en WhatsApp',
    score: (s, n) => `${s} de ${n}`,
    verdict: (s, n) => (s === n ? '¡Perfecto, eres un campeón del duala!' : s >= Math.ceil(n / 2) ? '¡Muy bien, sigue así!' : 'Ánimo, juega de nuevo para mejorar.'),
    shareText: (s, n, url) => `Saqué ${s} de ${n} en el quiz del diccionario duala. ¿Y tú?\n\n${url}`,
    loading: 'Preparando el quiz…', error: 'No se puede preparar el quiz ahora. Inténtalo más tarde.',
  },
  de: {
    title: 'Quiz zum Duala-Wörterbuch',
    question: 'Welches Duala-Wort passt zu diesem Bild?',
    right: 'Bravo!', wrong: 'Nicht ganz. Die richtige Antwort:', means: 'Bedeutung',
    next: 'Nächste Frage', finish: 'Mein Ergebnis', replay: 'Nochmal spielen', share: 'Auf WhatsApp teilen',
    score: (s, n) => `${s} von ${n}`,
    verdict: (s, n) => (s === n ? 'Perfekt, Sie sind ein Duala-Champion!' : s >= Math.ceil(n / 2) ? 'Sehr gut, weiter so!' : 'Nur Mut, spielen Sie noch einmal.'),
    shareText: (s, n, url) => `Ich habe ${s} von ${n} beim Duala-Wörterbuch-Quiz erreicht. Und Sie?\n\n${url}`,
    loading: 'Quiz wird vorbereitet…', error: 'Das Quiz kann gerade nicht vorbereitet werden. Bitte später erneut versuchen.',
  },
};
const MEANING_FIELD = { fr: 'french', en: 'english', es: 'spanish', de: 'german' };

// « kɛ́sɛ̀, -í (sens 1) » -> « kɛ́sɛ̀ » : premier mot du mot-tête, sans suffixes ni parentheses.
const shortWord = (head) => head.replace(/\([^)]*\)/g, ' ').split(/[,\s]+/).filter(Boolean)[0] || head;
const shuffle = (a) => { const r = [...a]; for (let i = r.length - 1; i > 0; i--) { const j = Math.floor(Math.random() * (i + 1)); [r[i], r[j]] = [r[j], r[i]]; } return r; };

async function buildRound() {
  const manifest = await fetch('/illustrations/manifest.json').then((r) => (r.ok ? r.json() : {}));
  // Une image qui illustre plusieurs mots serait ambigue : on ne garde que les images a mot unique.
  const perImage = {};
  Object.entries(manifest).forEach(([head, img]) => { (perImage[img] = perImage[img] || []).push(head); });
  const pool = Object.entries(perImage).filter(([, heads]) => heads.length === 1).map(([img, heads]) => ({ head: heads[0], img, word: shortWord(heads[0]) }));
  const picked = shuffle(pool).slice(0, ROUNDS);
  const questions = picked.map((q) => {
    const wrong = shuffle(pool.filter((p) => p.word !== q.word)).slice(0, 2).map((p) => p.word);
    const answers = shuffle([q.word, ...wrong]);
    return { ...q, answers, correct: answers.indexOf(q.word) };
  });
  // Les sens (dans les 4 langues) viennent de la base, en une seule requete.
  let entries = {};
  try {
    const res = await dictionaryService.getIllustrated({ words: questions.map((q) => q.head), limit: 100 });
    (res.data.entries || []).forEach((e) => { entries[e.duala] = e; });
  } catch { entries = {}; }
  return questions.map((q) => ({ ...q, entry: entries[q.head] || null }));
}

function Quiz() {
  const [language, setLanguage] = useState('fr');
  const [questions, setQuestions] = useState(null);
  const [error, setError] = useState(false);
  const [index, setIndex] = useState(0);
  const [selected, setSelected] = useState(null);
  const [score, setScore] = useState(0);
  const t = TEXTS[language];

  const start = useCallback(() => {
    setQuestions(null); setError(false); setIndex(0); setSelected(null); setScore(0);
    buildRound().then((q) => (q.length ? setQuestions(q) : setError(true))).catch(() => setError(true));
  }, []);
  useEffect(() => { start(); }, [start]);

  const finished = questions && index >= questions.length;
  useEffect(() => {
    if (finished) trackQuizPlay(score, questions.length);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [finished]);
  const current = questions && !finished ? questions[index] : null;

  const onAnswer = (i) => {
    if (selected !== null) return;
    setSelected(i);
    if (i === current.correct) setScore((s) => s + 1);
  };
  const next = () => { setSelected(null); setIndex((i) => i + 1); };
  const share = () => window.open(`https://wa.me/?text=${encodeURIComponent(t.shareText(score, questions.length, `${window.location.origin}/quiz`))}`, '_blank', 'noopener,noreferrer');

  const meaning = current && current.entry ? current.entry[MEANING_FIELD[language]] || current.entry.french : null;

  return (
    <Box sx={{ maxWidth: 560, mx: 'auto', py: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 800 }}>{t.title}</Typography>
        <ToggleButtonGroup size="small" exclusive value={language} onChange={(_, v) => v && setLanguage(v)} aria-label="language">
          {['fr', 'en', 'es', 'de'].map((l) => <ToggleButton key={l} value={l} sx={{ textTransform: 'uppercase', px: 1.2 }}>{l}</ToggleButton>)}
        </ToggleButtonGroup>
      </Box>

      {error && <Alert severity="warning">{t.error}</Alert>}
      {!questions && !error && <Box sx={{ textAlign: 'center', py: 6 }}><CircularProgress /><Typography sx={{ mt: 2 }}>{t.loading}</Typography></Box>}

      {current && (
        <>
          <QuizCard
            number={index + 1}
            question={t.question}
            image={current.img}
            answers={current.answers}
            selected={selected}
            correct={current.correct}
            onAnswer={onAnswer}
          />
          {selected !== null && (
            <Box sx={{ mt: 2, textAlign: 'center' }}>
              <Typography sx={{ fontWeight: 700, color: selected === current.correct ? 'success.main' : 'error.main' }}>
                {selected === current.correct ? t.right : `${t.wrong} ${current.word}`}
              </Typography>
              {meaning && <Typography variant="body2" color="text.secondary">{t.means} : {meaning}</Typography>}
              <Button variant="contained" endIcon={<NextIcon />} onClick={next} sx={{ mt: 1.5, textTransform: 'none', fontWeight: 700 }}>
                {index + 1 >= questions.length ? t.finish : t.next}
              </Button>
            </Box>
          )}
        </>
      )}

      {finished && (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="h3" sx={{ fontWeight: 900, color: 'warning.dark' }}>{t.score(score, questions.length)}</Typography>
          <Typography sx={{ my: 1.5 }}>{t.verdict(score, questions.length)}</Typography>
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button variant="contained" startIcon={<WhatsAppIcon />} onClick={share} sx={{ textTransform: 'none', fontWeight: 700, bgcolor: '#25d366', '&:hover': { bgcolor: '#1da851' } }}>{t.share}</Button>
            <Button variant="outlined" startIcon={<ReplayIcon />} onClick={start} sx={{ textTransform: 'none', fontWeight: 700 }}>{t.replay}</Button>
          </Box>
        </Box>
      )}
    </Box>
  );
}

export default Quiz;
